import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as InfoIcon } from 'assets/icons/info.svg';
import { ReactComponent as PlusIcon } from 'assets/icons/plus.svg';
import { ReactComponent as SearchIcon } from 'assets/icons/search.svg';
import CardSession from 'components/CardSession/CardSession';
import useMonitoringSessions from 'components/MonitoringAgent/hooks/useMonitoringSessions';
import { isMonitoringSessionReadyForResults } from 'components/MonitoringAgent/utils/monitoringSessionFlowUtils';
import SmallLoader from 'components/SmallLoader/SmallLoader';
import TextField from 'components/TextField/TextField';

import type IMonitoringSessionsTab from './interfaces/IMonitoringSessionsTab';

import styles from './sass/MonitoringSessionsTab.module.scss';

function MonitoringSessionsTab({
  sessions,
  agentDefinition,
  allAvailableCards,
  isLoading = false,
  isError = false,
  isActionLoading = false,
  onNewSelection,
  onEditSession,
  onDeleteSession,
  onViewSessionResults,
}: IMonitoringSessionsTab): React.JSX.Element {
  const { t } = useTranslation();
  const [search, setSearch] = useState<string>('');

  const { filteredSessionCards } = useMonitoringSessions({
    sessions,
    agentDefinition,
    allAvailableCards,
    search,
  });

  return (
    <div className={styles.container}>
      <div className={styles.infoBox}>
        <InfoIcon aria-hidden="true" />
        <p className={styles.description}>
          {t(agentDefinition.sessionsInfoKey)}
        </p>
      </div>

      <TextField
        value={search}
        name="monitoringSessionsSearch"
        placeholder={t('valueChainMap.monitoringHubSearchPlaceholder')}
        icon={<SearchIcon />}
        className={styles.searchField}
        onChange={({ target }) => setSearch(target.value)}
      />

      <button
        type="button"
        className={styles.newSelectionButton}
        onClick={onNewSelection}
        disabled={isActionLoading}
        data-i18n-key="valueChainMap.monitoringHubNewSelection"
      >
        <PlusIcon aria-hidden="true" />
        <span>{t('valueChainMap.monitoringHubNewSelection')}</span>
      </button>

      <div className={styles.sessionsList}>
        {isLoading ? (
          <div className={styles.loaderWrapper}>
            <SmallLoader size={30} />
          </div>
        ) : null}

        {!isLoading && isError ? (
          <p className={styles.stateMessage}>{t('valueChainMap.aiSessionsLoadError')}</p>
        ) : null}

        {!isLoading && !isError && !filteredSessionCards.length ? (
          <p className={styles.stateMessage}>{t('valueChainMap.aiSessionsEmpty')}</p>
        ) : null}

        {!isLoading && !isError && filteredSessionCards.map(({ session, title, entities }) => (
          <CardSession
            key={session.id}
            title={title}
            entities={entities}
            isActive={session.isActive}
            isDisabled={isActionLoading}
            showViewResults={isMonitoringSessionReadyForResults(session)}
            onViewResults={onViewSessionResults ? () => onViewSessionResults(session) : undefined}
            onEdit={() => onEditSession(session)}
            onDelete={() => onDeleteSession(session)}
          />
        ))}
      </div>
    </div>
  );
}

export default MonitoringSessionsTab;
